/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3149InitLU, U3149WorkNatureSearchRow } from './U3149Service';

export type InitLU = U2000LUState['personalization'] &
  U3149InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3149ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3149WorkNatureSearchRow>;
  initLU: U3149InitLU;
}

interface U3149ContextProviderProps {
  initLU: U3149InitLU;
  children: ReactNode;
}

const U3149Context = React.createContext<U3149ContextType | undefined>(undefined);

export const U3149ContextProvider: React.FC<U3149ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3149',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3149WorkNatureSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U3149ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3149Context.Provider value={contextValue}>{children}</U3149Context.Provider>;
};

export function useU3149Context(): U3149ContextType {
  const context = useContext(U3149Context);
  if (!context) {
    throw new Error('useU3149Context must be used within a U3149ContextProvider');
  }
  return context;
}
